import React, { useState, useEffect, useCallback } from "react";
import { Bell, Mail, Smartphone, Monitor, RefreshCw, RotateCcw, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNEL_ICONS = { email: Mail, push: Smartphone, in_app: Monitor };

export default function NotificationPreferencesPage() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [clientPrefs, setClientPrefs] = useState(null);

  const fetchPrefs = useCallback(async () => {
    const res = await fetch(`${API}/api/notification-preferences`, { credentials: "include" });
    if (res.ok) setPrefs(await res.json());
    const cres = await fetch(`${API}/api/client-notifications/preferences`, { credentials: "include" });
    if (cres.ok) setClientPrefs(await cres.json());
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const toggle = (category, channel) => {
    if (!prefs) return;
    const updated = { ...prefs, categories: { ...prefs.categories } };
    updated.categories[category] = { ...updated.categories[category], [channel]: !updated.categories[category][channel] };
    setPrefs(updated);
  };

  const toggleCompliance = async () => {
    if (!clientPrefs) return;
    const next = !clientPrefs.compliance_updates;
    setClientPrefs({ ...clientPrefs, compliance_updates: next });
    try {
      await fetch(`${API}/api/client-notifications/preferences`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ compliance_updates: next }),
      });
      setMessage(next ? "Compliance updates enabled" : "Compliance updates disabled");
      setTimeout(() => setMessage(""), 3000);
    } catch (e) {
      setClientPrefs({ ...clientPrefs, compliance_updates: !next });
    }
  };

  const save = async () => {
    setSaving(true);
    const res = await fetch(`${API}/api/notification-preferences`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ categories: prefs.categories }),
    });
    if (res.ok) setMessage("Preferences saved");
    setSaving(false);
    setTimeout(() => setMessage(""), 3000);
  };

  const reset = async () => {
    await fetch(`${API}/api/notification-preferences/reset`, { method: "POST", credentials: "include" });
    fetchPrefs();
    setMessage("Reset to defaults");
    setTimeout(() => setMessage(""), 3000);
  };

  if (!prefs) return <div className="min-h-screen bg-[#0D0F14] p-6 text-[#8B95A5]">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#0D0F14] text-white p-6 lg:p-10" data-testid="notification-prefs-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-3">
          <Bell size={24} className="text-[#C9A84C]" /> Notification Preferences
        </h1>
        <div className="flex gap-2">
          <button onClick={reset} className="px-3 py-1.5 bg-[rgba(255,255,255,0.06)] rounded text-sm text-[#8B95A5] hover:text-white flex items-center gap-1"
            data-testid="reset-prefs-btn"><RotateCcw size={14} /> Reset</button>
          <button onClick={save} disabled={saving}
            className="px-4 py-1.5 bg-[#C9A84C] text-[#0D0F14] rounded text-sm font-semibold disabled:opacity-50"
            data-testid="save-prefs-btn">{saving ? "Saving..." : "Save"}</button>
        </div>
      </div>

      {message && <p className="text-xs text-[#4ADE80] mb-4" data-testid="prefs-message">{message}</p>}

      {/* Compliance update opt-in */}
      {clientPrefs && (
        <div className="mb-6 rounded-xl border border-[rgba(255,255,255,0.06)] bg-[#132340] p-5" data-testid="compliance-optin-card">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-[#00E5C8] mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-bold text-white">Compliance document updates</div>
                <div className="text-[11px] text-[#8B95A5] mt-1 max-w-xl">
                  Notify me when compliance documents affecting my account are updated. Emails are sent
                  from <code className="text-[#00C8E0]">nonreply@charstan.com</code> and are delivered in
                  English or Portuguese based on your account language.
                </div>
              </div>
            </div>
            <button
              onClick={toggleCompliance}
              className={`shrink-0 w-12 h-6 rounded-full transition-colors ${clientPrefs.compliance_updates ? "bg-[#4ADE80]" : "bg-[#333]"}`}
              data-testid="toggle-compliance-updates"
              aria-label="Toggle compliance update notifications"
            >
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${clientPrefs.compliance_updates ? "translate-x-6" : "translate-x-0.5"}`} />
            </button>
          </div>
          <div className="text-[10px] text-[#8B95A5] mt-3">
            Status: <span className={clientPrefs.compliance_updates ? "text-[#4ADE80] font-bold" : "text-[#8B95A5]"} data-testid="compliance-updates-status">
              {clientPrefs.compliance_updates ? "OPTED IN" : "OPTED OUT"}
            </span>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-[rgba(255,255,255,0.06)] overflow-hidden">
        <table className="w-full" data-testid="prefs-table">
          <thead>
            <tr className="bg-[rgba(255,255,255,0.03)]">
              <th className="text-left py-3 px-4 text-[10px] text-[#8B95A5] uppercase tracking-wider">Category</th>
              <th className="text-center py-3 px-4 text-[10px] text-[#8B95A5] uppercase tracking-wider"><Mail size={12} className="inline mr-1" />Email</th>
              <th className="text-center py-3 px-4 text-[10px] text-[#8B95A5] uppercase tracking-wider"><Smartphone size={12} className="inline mr-1" />Push</th>
              <th className="text-center py-3 px-4 text-[10px] text-[#8B95A5] uppercase tracking-wider"><Monitor size={12} className="inline mr-1" />In-App</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(prefs.categories).map(([cat, settings]) => (
              <tr key={cat} className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.02)]">
                <td className="py-3 px-4 text-sm text-white">{settings.label || cat}</td>
                {["email", "push", "in_app"].map(ch => (
                  <td key={ch} className="py-3 px-4 text-center">
                    <button onClick={() => toggle(cat, ch)}
                      className={`w-10 h-5 rounded-full transition-colors ${settings[ch] ? "bg-[#4ADE80]" : "bg-[#333]"}`}
                      data-testid={`toggle-${cat}-${ch}`}>
                      <div className={`w-4 h-4 rounded-full bg-white transition-transform ${settings[ch] ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
